import React, { useEffect, useState } from 'react';
import { MdInfoOutline, MdWarningAmber } from 'react-icons/md';
import { useFormContext } from '../../../context/FormContext';
import { SignupLayout } from '../../ui/SignupLayout';
import { Spinner } from 'reactstrap';
import { updateQuote, savePolicy, issuePolicy } from '../../services/QuoteService';
import { generateNmDocument } from '../../services/DocumentService';
import { salePayment } from '../../services/PaymentService';
import Session from '../../auth/Session';

const CREDIT_CARD_FEE = 0.03;

/*
 * Payment flow (token-based, payment-first):
 *
 *   1. SALE    - charge the customer using the single-use payment_token.
 *                Card and ACH both run a single sale (card sale = auth+capture in one).
 *   2. ISSUE   - issue the policy via NormanMax. Payment has ALREADY been collected,
 *                so if issuance fails the customer HAS been charged and must contact
 *                support (we do not auto-refund here).
 *   3. PERSIST - save policy to DB, generate documents, final quote update.
 *
 * Payment data arrives via router state as a single-use token only. We never see, forward, or store raw card / bank details anywhere.
 */
export function ProcessingPayment({ history }) {
    const { formData, setFields } = useFormContext();

    const paymentToken = history.location?.state?.paymentToken || '';
    const paymentType = history.location?.state?.paymentType || '';

    // 'processing' | 'error'
    const [status, setStatus] = useState('processing');
    const [errorMessage, setErrorMessage] = useState('');
    // When true, the customer's card has been charged and they must NOT retry.
    const [charged, setCharged] = useState(false);

    useEffect(() => {
        let cancelled = false;

        // Guard: if we somehow landed here without a token, bounce back
        if (!paymentToken) {
            const returnRoute = paymentType === 'creditCard' ? '/signup/payment/card' : '/signup/payment/bank';
            history.replace(returnRoute, { paymentError: true, paymentType });
            return;
        }

        async function run() {
            // Compute the amount ONCE so the figure we charge and save can never diverge.
            const selectedOption = formData.RateOptions?.find(
                (o) => o.trigger_type === formData.SelectedTriggerType
            );
            const basePremium = selectedOption?.total_amount_due || formData.SelectedTotalPremium || 0;
            const isCard = paymentType === 'creditCard';
            const amount = isCard
                ? Math.round(basePremium * (1 + CREDIT_CARD_FEE) * 100) / 100
                : basePremium;

            // Idempotency key generated on the payment-details screen and carried in formData.
            // Used as NMI orderid + our own dedupe key.
            const idempotencyKey = formData.PaymentIdempotencyKey || '';

            // Local flag mirrors the `charged` state but is readable synchronously inside
            // this closure (the `charged` state value here is stale after setCharged).
            let hasCharged = false;

            try {
                // Payment-first flow:
                //   CARD: sale (charge now) -> issue policy
                //   ACH:  sale (submit)     -> issue policy
                // If the payment fails, we never attempt issuance.
                // If issuance fails AFTER payment, the customer HAS been charged and
                // must contact support (we do not auto-refund here).

                // STEP 1: TAKE PAYMENT
                const sale = await salePayment({
                    paymentToken,
                    amount,
                    idempotencyKey,
                    formData,
                });
                if (cancelled) return;

                if (!sale.success) {
                    // Payment declined/failed. Customer not charged.
                    // Send them back to re-enter payment details.
                    const returnRoute = isCard ? '/signup/payment/card' : '/signup/payment/bank';
                    history.push(returnRoute, {
                        paymentError: true,
                        paymentErrorMessage: sale.errorMessage || '',
                        paymentType,
                    });
                    return;
                }

                // Payment has succeeded - from here on the customer HAS been charged.
                hasCharged = true;
                setCharged(true);

                // STEP 2: ISSUE POLICY (payment already collected)
                const issuePolicyResult = await issuePolicy(buildIssuePolicyPayload(formData));
                if (cancelled) return;

                const issueStatus = (issuePolicyResult.status || '').toLowerCase();

                if (!issuePolicyResult.success && issueStatus === 'referred') {
                    // Payment collected and carrier referred for manual underwriting.
                    // This is a non-error outcome: persist status and move to completion screen.
                    try {
                        if (formData.QuoteId) {
                            await updateQuote(formData.QuoteId, {
                                ...formData,
                                currentStep: 'complete',
                                status: 'Referred',
                            });
                        }
                    } catch (updateErr) {
                        console.error('Failed to update quote on referred issue result:', updateErr);
                    }

                    setFields({
                        Status: 'Referred',
                        currentStep: 'complete',
                        PolicyNumber: '',
                        ExpiryDate: '',
                    });

                    history.push('/signup/complete');
                    return;
                }

                if (!issuePolicyResult.success) {
                    // Payment collected but policy issuance failed.
                    // Do NOT let the customer retry - they've already paid.
                    setErrorMessage(
                        issuePolicyResult.error_message ||
                            'Your payment was collected, but we were unable to issue your policy at this time. Please contact support and we will resolve this for you.'
                    );
                    setStatus('error');
                    return;
                }

                // STEP 3: Persist policy to DB
                try {
                    await savePolicy(formData, {
                        ...issuePolicyResult,
                        total_premium: basePremium,
                    });
                } catch (saveErr) {
                    console.error('Failed to persist policy to DB:', saveErr);
                }

                // Generate DES document
                try {
                    const sessionToken = Session.getToken();
                    await generateDes(formData.QuoteId, sessionToken);
                } catch (desErr) {
                    console.error('Failed to generate DES document:', desErr);
                }

                // POLICY / INVOICE documents (fire-and-forget; server-side delay)
                if (formData.QuoteId) {
                    generateNmDocument(formData.QuoteId, 'POLICY', 60).catch((err) =>
                        console.error('Failed to generate policy document:', err)
                    );
                    generateNmDocument(formData.QuoteId, 'INVOICE', 10).catch((err) =>
                        console.error('Failed to generate invoice document:', err)
                    );
                }

                // Final quote update
                if (formData.QuoteId) {
                    try {
                        await updateQuote(formData.QuoteId, {
                            ...formData,
                            currentStep: 'complete',
                            status: 'Active',
                            PolicyNumber: issuePolicyResult.policy_number || '',
                            ExpiryDate: issuePolicyResult.expiry_date || '',
                        });
                    } catch (updateErr) {
                        console.error('Failed to update quote on policy issue:', updateErr);
                    }
                }

                setFields({
                    Status: 'Active',
                    currentStep: 'complete',
                    PolicyNumber: issuePolicyResult.policy_number || '',
                    ExpiryDate: issuePolicyResult.expiry_date || '',
                });

                history.push('/signup/complete');
            } catch (err) {
                if (cancelled) return;
                if (hasCharged) {
                    // Something threw after we took payment - treat as a post-payment
                    // issuance/processing failure so the "contact support" UI shows.
                    setErrorMessage(
                        'Your payment was collected, but something went wrong while issuing your policy. Please contact support and we will resolve this for you.'
                    );
                } else {
                    setErrorMessage(err.message || 'An unexpected error occurred while processing your payment.');
                }
                setStatus('error');
            }
        }

        run();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <SignupLayout showProgress={false}>
            {status === 'processing' && (
                <>
                    <h1 className="text-h1" style={{ textAlign: 'center', marginTop: 0 }}>
                        Processing payment
                    </h1>
                    <p className="text-default" style={{ textAlign: 'center' }}>
                        We're securely verifying your payment details.
                    </p>

                    <div className="dp-loading" aria-label="Processing payment…">
                        <Spinner style={{color: `var(--brand-primary)`}}/>
                    </div>

                    <div className="security-banner" style={{ borderColor: 'var(--brand-primary)' }}>
                        <MdInfoOutline size={20} className="security-banner__icon" />
                        <div className="security-banner__text">
                            <strong>Please don't close this window</strong>
                            <p>
                                This process may take up to <strong>30 seconds.</strong>
                            </p>
                        </div>
                    </div>
                </>
            )}

            {status === 'error' && (
                <div className="processing-error">
                    <div className="processing-error__banner">
                        <MdWarningAmber size={24} className="processing-error__icon" />
                        <strong>Something went wrong</strong>
                    </div>
                    <p className="processing-error__message">{errorMessage}</p>
                    <div className="processing-error__help">
                        {charged ? (
                            <>
                                <p>
                                    Your payment has been collected. Please do not attempt to
                                    resubmit - contact our team immediately and we'll get this
                                    resolved for you.
                                </p>
                                <p>
                                    Call us at{' '}
                                    <a href="tel:8772695422">
                                        <strong>877-269-5422</strong>
                                    </a>
                                </p>
                            </>
                        ) : (
                            <>
                                <p>
                                    You have not been charged. If you continue to have trouble,
                                    please contact our team and we'll be glad to help.
                                </p>
                                <p>
                                    Call us at{' '}
                                    <a href="tel:8772695422">
                                        <strong>877-269-5422</strong>
                                    </a>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </SignupLayout>
    );
}

// Build issuePolicy payload
function buildIssuePolicyPayload(formData) {
    return {
        quoteNumber: formData.QuoteNumber,
        selectedTriggerType: formData.SelectedTriggerType,
        producingAgent: {
            name: 'Paul Schrembeck',
            emailAddress: 'support@elevanasolutions.com',
        },
        additionalParties:
            formData.HasAdditionalParties === true && formData.AdditionalPartyType !== ''
                ? [
                      {
                          type: formData.AdditionalPartyType || '',
                          name:
                              formData.AdditionalPartyName !== ''
                                  ? formData.AdditionalPartyName
                                  : [formData.AdditionalPartyFirstName, formData.AdditionalPartyLastName]
                                        .filter(Boolean)
                                        .join(' '),
                          address: formData.AdditionalPartyAddressOne || '',
                          city: formData.AdditionalPartyCity || '',
                          state: formData.AdditionalPartyState || '',
                          zipCode: formData.AdditionalPartyZipCode || '',
                          country: formData.AdditionalPartyCountry || 'US',
                      },
                  ]
                : null,
        mailingSameAsProperty: formData.MailingSameAsProperty,
        mailingAddressOne: formData.ContactAddressOne,
        mailingCity: formData.ContactCity,
        mailingState: formData.ContactState,
        mailingZipCode: formData.ContactZipCode,
        targetAddressOne: formData.TargetAddressOne,
        targetCity: formData.TargetCity,
        targetState: formData.TargetState,
        targetZipCode: formData.TargetZipCode,
    };
}

async function generateDes(quoteId, token) {
    if (!quoteId) return;

    const response = await fetch(`api/Document/GenerateDes/${quoteId}`, {
        method: 'POST',
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });

    if (!response.ok) {
        throw new Error(`GenerateDes failed: ${response.status}`);
    }

    return response.json();
}